import logging from "utils/logging";
import React, { useEffect } from "react";
import { styled, keyframes } from "@mui/material/styles";
import useSound from "use-sound";
import dailyDoubleSound from "sounds/daily-double.mp3"; // TODO: Replace with Groupardy-branded sound via ElevenLabs Sound Effects API
import { Typography } from "@mui/material";
import {
  GAMESTATE_ROUNDSTATE_QUESTION_ANSWERING,
  GAMESTATE_ROUNDSTATE_QUESTION_JUDGING,
  GAMESTATE_ROUNDSTATE_QUESTION_RESULTS,
} from "../../gamestate/constants";

const log = logging.getLogger("Daily Double - Gameboard Screen"); // eslint-disable-line no-unused-vars

const Root = styled('div')(({ theme }) => ({
  width: '100%',
}));

const WagerText = styled(Typography)(({ theme }) => ({
  paddingTop: theme.spacing(5),
  fontFamily: "'Poppins', 'Inter', sans-serif",
  fontWeight: 600,
}));

const ResultText = styled(Typography)({
  fontFamily: "'Poppins', 'Inter', sans-serif",
  fontWeight: 600,
  fontSize: "1em",
});

const GuessText = styled('span')({
  fontFamily: "'Poppins', 'Inter', sans-serif",
});

const JudgementText = styled(Typography)({
  fontFamily: "'Poppins', 'Inter', sans-serif",
  fontWeight: 600,
  fontSize: "0.7em",
});

const PlusPoints = styled('span')({
  color: "#4ce74c",
});

const MinusPoints = styled('span')({
  color: "#e74c4c",
});

const CorrectAnswer = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontFamily: "'Poppins', 'Inter', sans-serif",
  fontSize: "0.7em",
}));

const CorrectAnswerLabel = styled('span')({
  fontFamily: "'Poppins', 'Inter', sans-serif",
  fontWeight: 600,
});

const ddScaleIn = keyframes`
  0% { transform: scale(0.3); opacity: 0; }
  70% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const ddFadeIn = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;

const DDRevealContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '300px',
});

const DDRevealText = styled('div')({
  fontFamily: "'Poppins', 'Inter', sans-serif",
  fontSize: '5rem',
  fontWeight: 700,
  color: '#ffe066',
  textShadow: '0 0 20px rgba(46, 196, 182, 0.6), 0 0 40px rgba(46, 196, 182, 0.3)',
  animation: `${ddScaleIn} 0.6s ease-out forwards`,
  lineHeight: 1.1,
});

const DDRevealSubtext = styled('div')({
  fontFamily: "'Poppins', 'Inter', sans-serif",
  fontSize: '2rem',
  color: '#2ec4b6',
  animation: `${ddFadeIn} 0.4s ease-out 0.3s forwards`,
  opacity: 0,
  marginTop: '1rem',
});

const DDRevealBox = () => {
  const [playDDSound] = useSound(dailyDoubleSound);

  useEffect(() => {
    log.debug("Playing DD sound");
    playDDSound();
  }, [playDDSound]);

  return (
    <DDRevealContainer>
      <DDRevealText>DAILY DOUBLE!</DDRevealText>
      <DDRevealSubtext>Get ready...</DDRevealSubtext>
    </DDRevealContainer>
  );
};

const DDWager = ({ playerName, wager }) => {
  let prompt = playerName
    ? `${playerName}, submit your wager:`
    : "Submit your wager:";

  let wagerEntry = <div />;
  if (wager) {
    wagerEntry = (
      <WagerText variant="h3">
        ${wager.toLocaleString()}
      </WagerText>
    );
  }

  return (
    <Root>
      <WagerText variant="h3">
        {prompt}
      </WagerText>
      {wagerEntry}
    </Root>
  );
};

const DDResult = ({ gameState, guess, playerName, answer, correct, wager }) => {
  if (!gameState.dailyDouble) {
    return null;
  }

  let visibleStates = [
    GAMESTATE_ROUNDSTATE_QUESTION_ANSWERING,
    GAMESTATE_ROUNDSTATE_QUESTION_JUDGING,
    GAMESTATE_ROUNDSTATE_QUESTION_RESULTS,
  ];

  if (!visibleStates.includes(gameState.roundState)) {
    return null;
  }

  let guessLabel = playerName
    ? `${playerName.toUpperCase()}'S GUESS: `
    : `GUESS: `;

  let judgement = null;

  if (gameState.roundState === GAMESTATE_ROUNDSTATE_QUESTION_RESULTS) {
    if (correct) {
      judgement = (
        <JudgementText variant="h6">
          ✅ Correct!{" "}
          <PlusPoints>
            +${Math.abs(wager).toLocaleString()}
          </PlusPoints>
        </JudgementText>
      );
    } else {
      judgement = (
        <div>
          <div style={{ margin: "8px" }} />
          <JudgementText variant="h6">
            ❌ &nbsp;Incorrect!{" "}
            <MinusPoints>
              -${Math.abs(wager).toLocaleString()}
            </MinusPoints>
          </JudgementText>
          <CorrectAnswer variant="h6">
            <CorrectAnswerLabel>Correct Answer:</CorrectAnswerLabel>{" "}
            {answer}
          </CorrectAnswer>
        </div>
      );
    }
  }

  return (
    <ResultText variant="h5">
      <p>
        {guessLabel} <GuessText>{guess}</GuessText>
      </p>
      {judgement}
    </ResultText>
  );
};

export { DDRevealBox, DDWager, DDResult };
